import Image, { type StaticImageData } from "next/image";
import { getGradientClass, getHeroXPaddingClass } from "~/styles/styles";
import Section from "../../common/section";

type BackgroundHeroProps = {
  bgColour?: string;
  smallTitle?: string | JSX.Element;
  title?: string | JSX.Element;
  pretitle?: string | JSX.Element;
  body?: string | string[] | JSX.Element;

  image?: string | StaticImageData;
  imageClass?: string; // extra image class add to image position
  bgImage?: string | StaticImageData;
  bgImagePosition?: "left" | "center" | "right" | "left-more" | "right-more" | "righter" | "lefter"; // i.e. left: width 1/2, lefter: width 2/3 left-more width 3/4
  bgImageClass?: string;
  bgImageFit?: "auto" | "full"; // image stretch to fit section on width updates, by default no width is define
  bgImageContainClass?: string;
  bgImageFlexHeight?: boolean; // image do not stretch to fit section
  imageOpacity?: string; // image object - might need extra class to image itself
  imgRight?: boolean; // text remain, heart image right

  actionGroup?: any;
  customDiv?: JSX.Element;

  right?: boolean; // text right, image right
  padding?: string;
  extraPadding?: string; // add on top of existing padding classes
  bodyWidth?: string;
  custom?: boolean; // mobile: image top, text left, actionGroup? | standard: image bottom left, text right, no actionGroup
  maxWidth?: string; // section max width

  flexCenter?: boolean; // title text align center
  mxDivClass?: string; // default margin-auto
  customSectionHeight?: string;
  fixedHeight?: boolean;
};

export const Xpadding = `ml:px-12 lg:px-16 xl:px-[4.2rem] 2xl:px-[3.2rem]`;

const BackgroundHero = (props: BackgroundHeroProps) => {
  const body = Array.isArray(props.body)
    ? props.body.map((line, i) => (
        <p key={i} className="mt-4 lg:mt-6 xl:mt-8">
          {line}
        </p>
      ))
    : props.body;

  // background image class --- custom size instead of using w-ful due to required image display (visual size) according to design
  const bgImageFit = props.bgImageFit === "auto" ? "md:w-auto" : props.bgImageFit === "full" ? "md:w-full" : "";

  const bgImageShareClass1 = `top-0 -left-16 -right-16 mx-auto max-w-[28rem] md:max-w-full ${bgImageFit} ${props.customDiv ? `xs:max-w-[34rem] ${props.bgImageFlexHeight ?? "md:h-full"}` : `xs:max-w-[44rem] ${props.bgImageFlexHeight ?? "xs:h-full"}`}`;
  const bgImageShareClass2 = `top-0 -left-16 -right-16 mx-auto max-w-[27rem] ${bgImageFit}  ${props.customDiv ? `xs:max-w-[34rem] md:max-w-[45rem]${props.bgImageFlexHeight ?? "md:h-full"}` : `xs:max-w-none xs:right-0 xs:-left-80 ${props.bgImageFlexHeight ?? "xs:h-full"}`}`;

  const position = props.bgImagePosition;

  const bgImagePosition =
    position === "right"
      ? `md:left-0 md:-right-80 lg:-right-[38rem] xl:-right-[41rem] ${bgImageShareClass1}`
      : position === "left"
        ? `md:right-0 md:-left-80 lg:-left-[38rem] xl:-left-[41rem] ${bgImageShareClass1}`
        : position === "left-more"
          ? `sm:-left-28 md:right-0 md:-left-80 xl:-left-[26rem] ${bgImageShareClass2}`
          : position === "right-more"
            ? `md:left-0 md:-right-80 xl:-right-[26rem] ${bgImageShareClass2}`
            : position === "righter"
              ? `md:left-0 md:-right-80 lg:-right-[38rem] xl:-right-[30rem] ${bgImageShareClass1}`
              : position === "lefter"
                ? `md:right-0 md:-left-80 lg:-left-[38rem] xl:-left-[30rem] ${bgImageShareClass1}`
                : position === "center"
                  ? "top-0 -left-16 -right-16 max-w-[32rem] mx-auto xs:max-w-[40rem] md:-left-28 md:max-w-full md:h-full lg:-left-36 object-cover"
                  : `${props.bgImageClass}`;

  const applyXpadding = position === "right" || position === "righter" || position === "right-more" ? Xpadding : "";
  const padding = props.padding
    ? props.padding
    : position === "center"
      ? `pb-16 pt-[5.2rem] md:pb-36 md:pt-8 ${applyXpadding} ${props.extraPadding}`
      : position === "left" || position === "right"
        ? `${props.body ? `pb-16 pt-[5.2rem] xs:pb-20 xs:pt-16 md:pb-28 ${applyXpadding} ${props.extraPadding}` : `pb-16 pt-[5.2rem] xs:py-20 md:py-16 lg:py-24 xl:py-28 ${applyXpadding} ${props.extraPadding}`} `
        : position === "left-more" || position === "right-more"
          ? `pb-16 pt-[5.2rem] xs:pb-20 xs:pt-16 md:pb-28 ${applyXpadding} ${props.extraPadding}`
          : position === "lefter" || position === "righter"
            ? `pb-16 pt-[5.2rem] xs:pb-20 xs:pt-16 md:py-16 lg:py-24 xl:py-28 ${applyXpadding} ${props.extraPadding}`
            : `pb-16 pt-[5.2rem] xs:pb-20 xs:pt-16 md:pb-28 ${applyXpadding} ${props.extraPadding}`;

  const sectionHeightClass = `${props.customSectionHeight ?? "min-h-[min(50vh,400px)] md:min-h-[26rem] lg:min-h-[28rem] xl:min-h-[30rem] 2xl:min-h-[34rem]"} `;

  // icon image position
  const imagePositionL = `-left-16 md:-left-20 lg:-left-24 2xl:-left-32 ${props.imageClass ?? ""}`;
  const imagePositionR = `-right-16 md:-right-20 lg:-right-24 2xl:-right-32 ${props.imageClass ?? ""}`;
  const imagePosition = `${(props.right ?? props.imgRight) ? imagePositionR : imagePositionL} ${props.imgRight === false && props.right ? imagePositionL : imagePositionR}`;

  return (
    <Section className={`${props.bgColour ?? getGradientClass("v-l-to-d")} flex overflow-hidden ${sectionHeightClass}`} flexHeight>
      <div className={`relative mx-auto w-full ${props.maxWidth ?? "max-w-7xl"} `}>
        {/* md:px-12 */}
        <div
          className={`container relative ${props.mxDivClass ?? "mx-auto"} h-full ${props.flexCenter ? "flex items-center" : props.customDiv ? "items-center justify-between gap-2 xl:flex" : ""} ${props.custom ? "px-8 xl:px-10 2xl:px-4" : getHeroXPaddingClass} ${padding}`}
        >
          {props.bgImage && (
            <Image
              src={props.bgImage}
              alt="background-image"
              height={1000}
              width={1000}
              className={`absolute object-top ${props.bgImageContainClass ?? "object-cover"} ${bgImagePosition} ${props.imageOpacity ?? ""}`}
            />
          )}
          <div className="z-20">
            <div
              className={`TEXTs relative mb-8 lg:mb-0 ${props.custom && props.right ? "lg:text-right" : !props.custom && props.right ? "text-right" : ""}`}
            >
              {typeof props.smallTitle === "string" ? (
                <div>
                  <h1 className={`mb-2 text-base/5 tracking-tight text-white lg:text-lg/6 xl:text-xl`}>{props.smallTitle}</h1>
                </div>
              ) : (
                props.smallTitle && (
                  <div className={`mb-2 text-base/5 tracking-tight text-white lg:text-lg/6 xl:text-xl`}>{props.smallTitle}</div>
                )
              )}

              {typeof props.title === "string" ? (
                <div>
                  <h1
                    className={`mb-2 font-bold text-white ${props.pretitle ? "" : "md:mb-4"} text-desk-resp-hero lg:leading-10 xl:text-5xl`}
                  >
                    {props.title}
                  </h1>
                </div>
              ) : (
                props.title && (
                  <div className={`mb-2 font-bold text-white ${props.pretitle ? "" : "md:mb-4"} text-desk-resp-hero lg:leading-10`}>
                    {props.title}
                  </div>
                )
              )}

              {typeof props.pretitle === "string" ? (
                <div>
                  <h2 className={`text-desk-resp-3xl mb-2 text-white xs:max-w-[30rem] ml:max-w-[34rem]`}>{props.pretitle}</h2>
                </div>
              ) : (
                props.pretitle && <div className={`text-desk-resp-3xl mb-2 text-white`}>{props.pretitle}</div>
              )}

              {props.body && (
                <div
                  className={`text-desk-resp-xl relative z-10 tracking-tight text-white ${props.bodyWidth ?? "w-[14.5rem] xs:w-[23rem] lg:w-[24rem] xl:w-[28rem]"} ${props.custom && props.right ? "lg:ml-auto" : !props.custom && props.right ? "ml-auto" : ""}`}
                >
                  {body}
                </div>
              )}
            </div>

            {props.actionGroup && <div className="relative bottom-0 left-0 z-10 text-white md:mb-6">{props.actionGroup}</div>}
          </div>

          {props.customDiv && (
            <div className="relative z-10 group overflow-hidden hover:overflow-x-scroll md:hover:overflow-hidden">{props.customDiv}</div>
          )}
        </div>

        {props.image && (
          <Image
            src={props.image}
            alt="heart-image"
            className={`absolute bottom-0 w-72 md:w-96 lg:w-[28rem] xl:w-[30rem] ${imagePosition}`}
          />
        )}
      </div>
    </Section>
  );
};

export default BackgroundHero;
